import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Drama, X } from "lucide-react";
import { SpotMap } from "../components/SpotMap";
import { SpotSheet } from "../components/SpotSheet";
import { TheatreMarker, NextShowCard, TheatreRadius, THEATRE_RADIUS } from "../components/TheatreShow";
import { api, formatLeft } from "../lib/api";
import { distanceMeters, formatDistance } from "../lib/geo";
import { useNow } from "../lib/useNow";

const SpotRow = ({ s, now, active, onClick }) => (
  <li>
    <button
      data-testid={`spot-list-item-${s.id}`}
      onClick={onClick}
      className={`w-full text-left flex items-center gap-3 rounded-xl px-3 py-2 transition-colors ${active ? "bg-stone-800" : "hover:bg-stone-800/60"}`}
    >
      <span className={`font-mono-x text-xs font-bold rounded-full px-2 py-0.5 ${s.is_mine ? "bg-emerald-500 text-stone-950" : s.status === "reserved" ? "bg-stone-700 text-amber-300" : "bg-[#E0533C] text-white"}`}>
        {formatLeft(s.leave_at, now)}
      </span>
      <span className="text-sm truncate flex-1">{s.address}</span>
      {s.dist != null && <span data-testid={`spot-distance-${s.id}`} className="text-[11px] font-mono-x text-amber-300">{formatDistance(s.dist)}</span>}
      {s.status === "reserved" && <span className="text-[10px] uppercase text-amber-400">{s.reserved_by_me ? "Tuo" : "Prenotato"}</span>}
    </button>
  </li>
);

export default function MapPage() {
  const [spots, setSpots] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [group, setGroup] = useState(null);
  const [theatreMode, setTheatreMode] = useState(false);
  const [flyTo, setFlyTo] = useState(null);
  const [theatre, setTheatre] = useState(null);
  const now = useNow();
  useEffect(() => { api.get("/theatre/next-show").then((r) => setTheatre(r.data)).catch(() => {}); }, []);

  const load = useCallback(() => api.get("/spots").then((r) => setSpots(r.data)).catch(() => {}), []);
  useEffect(() => {
    load();
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, [load]);

  const listed = useMemo(() => {
    if (!theatreMode || !theatre) return spots;
    const c = [theatre.theatre.lat, theatre.theatre.lng];
    return spots.map((s) => ({ ...s, dist: distanceMeters(c, [s.lat, s.lng]) }))
      .filter((s) => s.dist <= THEATRE_RADIUS).sort((a, b) => a.dist - b.dist);
  }, [spots, theatreMode, theatre]);

  const liveGroup = group?.filter((id) => spots.some((s) => s.id === id));
  const selected = spots.find((s) => s.id === selectedId);
  const available = listed.filter((s) => s.status === "active" && !s.is_mine);

  const selectOne = (s) => { setGroup(null); setSelectedId(s.id); };
  const selectGroup = (list) => { setGroup(list.map((s) => s.id)); setSelectedId(list[0].id); };
  const close = () => { setSelectedId(null); setGroup(null); };
  const step = (d) => {
    const i = liveGroup.indexOf(selectedId);
    setSelectedId(liveGroup[(i + d + liveGroup.length) % liveGroup.length]);
  };
  const enterTheatreMode = () => {
    setTheatreMode(true);
    setFlyTo([theatre.theatre.lat, theatre.theatre.lng]);
  };

  return (
    <div className="relative h-[calc(100vh-78px-var(--banner,0px))] min-h-[520px]">
      <SpotMap spots={spots} now={now} onSelect={selectOne} onSelectGroup={selectGroup} flyTo={flyTo} flyZoom={16} className="absolute inset-0 z-0">
        {theatre && <TheatreMarker data={theatre} />}
        {theatre && theatreMode && <TheatreRadius data={theatre} />}
      </SpotMap>
      {theatre?.next && !selected && !theatreMode && <NextShowCard data={theatre} now={now} onFindParking={enterTheatreMode} />}

      <aside className={`absolute z-[800] top-4 left-3 right-3 md:right-auto md:w-80 rounded-2xl bg-stone-900/85 backdrop-blur-xl border p-4 shadow-2xl rise ${theatreMode ? "border-amber-500/60" : "border-stone-700/60"}`}>
        {theatreMode && (
          <div data-testid="theatre-mode-banner" className="flex items-center justify-between gap-2 mb-2 rounded-xl bg-amber-500/15 px-3 py-2">
            <span className="text-xs font-semibold text-amber-200 inline-flex items-center gap-1.5">
              <Drama className="h-4 w-4" /> Entro {THEATRE_RADIUS} m dal Teatro
            </span>
            <button data-testid="theatre-mode-exit" onClick={() => setTheatreMode(false)} className="text-xs text-stone-300 hover:text-white inline-flex items-center gap-1 transition-colors">
              <X className="h-3.5 w-3.5" /> Tutti
            </button>
          </div>
        )}
        <div className="flex items-end justify-between">
          <div>
            <p className="font-mono-x text-[11px] uppercase tracking-wider text-amber-400">In arrivo · 60 min</p>
            <p className="font-display text-2xl font-extrabold"><span data-testid="available-count">{available.length}</span> {available.length === 1 ? "posto libero" : "posti liberi"}</p>
          </div>
          <Link to="/segnala" data-testid="map-signal-button" className="h-10 px-4 rounded-full bg-[#E0533C] hover:bg-[#C8432D] text-sm font-semibold inline-flex items-center gap-1 transition-colors">
            <Plus className="h-4 w-4" /> Libero
          </Link>
        </div>
        <ul data-testid="spot-list" className={`${selected ? "hidden md:block" : ""} mt-3 space-y-1.5 max-h-40 md:max-h-72 overflow-auto pr-1`}>
          {listed.length === 0 && (
            <li data-testid="spot-list-empty" className="text-sm text-stone-400 py-2">
              {theatreMode ? "Per ora nessuno lascia il posto vicino al Teatro. Riprova tra poco: la lista si aggiorna da sola." : "Nessuno sta per lasciare il posto al momento."}
            </li>
          )}
          {listed.map((s) => <SpotRow key={s.id} s={s} now={now} active={selectedId === s.id} onClick={() => selectOne(s)} />)}
        </ul>
      </aside>

      {selected && (
        <SpotSheet spot={selected} now={now} onClose={close} onChanged={load}
          group={liveGroup?.length > 1 ? { index: liveGroup.indexOf(selectedId), total: liveGroup.length, onPrev: () => step(-1), onNext: () => step(1) } : null} />
      )}
    </div>
  );
}
